import { useEffect, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { useAppSelector } from "../../../app/hooks";
import { getParamAsInt } from "../../../app/utils";
import AlertMessage, { AlertLevel } from "../../../components/AlertMessage";
import { ClassLinkOrCleverIconWithTooltip } from "../../../components/ClassLinkOrCleverIcon";
import { Loading } from "../../../components/Loading";
import {
	AdminEditCourseViewModel,
	CourseUserViewModel,
	useLazyGetEditCourseInfoQuery,
} from "../coursesApiSlice";
import CourseTeachersSection from "./CourseTeachersSection";
import CourseStudentsSection from "./CourseStudentsSection";
import CourseInformationSection from "./CourseInformationSection";

export default function EditCourseByAdmin() {
	const [queryParams] = useSearchParams();
	const courseId = getParamAsInt(queryParams, "courseId");

	const { config } = useAppSelector((state) => state.site);

	const [errorMessage, setErrorMessage] = useState("");
	const [showOutsideIcon, setShowOutsideIcon] = useState(false);

	const [courseInfo, setCourseInfo] = useState<AdminEditCourseViewModel>();
	const [teachers, setTeachers] = useState<CourseUserViewModel[]>([]);
	const [students, setStudents] = useState<CourseUserViewModel[]>([]);

	const [getCourseInfo, getCourseInfoResult] = useLazyGetEditCourseInfoQuery();

	const location = useLocation();
	const returnTo = location.state?.returnTo as string;

	useEffect(() => {
		if (courseId) {
			getCourseInfo(courseId)
				.unwrap()
				.then((payload) => {
					setCourseInfo(payload);
					setTeachers(payload.proctors);
					setStudents(payload.students);
				});
		}
	}, []);

	// Update teachers after adding a teacher to the course
	useEffect(() => {
		if (getCourseInfoResult.data?.proctors) {
			setTeachers(getCourseInfoResult.data.proctors);
		}
	}, [getCourseInfoResult.data?.proctors]);

	// Update students after adding a student to the course
	useEffect(() => {
		if (getCourseInfoResult.data?.students) {
			setStudents(getCourseInfoResult.data.students);
		}
	}, [getCourseInfoResult.data?.students]);

	// Update seat information when it changes
	useEffect(() => {
		if (getCourseInfoResult.data && courseInfo) {
			setCourseInfo({
				...courseInfo,
				seatsRemaining: getCourseInfoResult.data.seatsRemaining,
				limitedSeatLicenses: getCourseInfoResult.data.limitedSeatLicenses,
			});
		}
	}, [getCourseInfoResult.data?.seatsRemaining, getCourseInfoResult.data?.limitedSeatLicenses]);

	useEffect(() => {
		setShowOutsideIcon(!!courseInfo?.classLinkID || !!courseInfo?.cleverID);
	}, [courseInfo]);

	const tooltipContent = courseInfo?.cleverID
		? "This course has a Clever account"
		: courseInfo?.classLinkID
			? "This course has a ClassLink account"
			: undefined;

	return (
		<section>
			<h1>Manage Course</h1>
			<Loading loading={getCourseInfoResult.isLoading} />
			{courseInfo && courseId && (
				<section>
					<div className="flex mt-6 gap-x-3 items-center">
						{showOutsideIcon && !config?.isUtahComposeSite && (
							<ClassLinkOrCleverIconWithTooltip
								isClassLinkEntity={!!courseInfo.classLinkID}
								isCleverEntity={!!courseInfo.cleverID}
								iconClasses="size-10"
								tooltipId="outside-site-icon-tooltip"
								tooltipContent={tooltipContent}
							/>
						)}
						<h2>{courseInfo.editModel.name}</h2>
					</div>
					<AlertMessage
						className="mt-4 text-sm"
						type={AlertLevel.Error}
						message={errorMessage}
						showIcon={true}
						onClear={() => setErrorMessage("")}
					/>
					<CourseInformationSection
						courseInfo={courseInfo}
						addingNew={false}
						returnTo={returnTo}
						setErrorMessage={setErrorMessage}
						setCourseInfo={setCourseInfo}
					/>
					<CourseTeachersSection
						teachers={teachers}
						courseId={courseId}
						schoolId={courseInfo.schoolId}
						districtId={courseInfo.districtId}
					/>
					<CourseStudentsSection
						students={students}
						courseId={courseId}
						schoolId={courseInfo.schoolId}
						districtId={courseInfo.districtId}
						seatsRemaining={courseInfo.seatsRemaining}
						showSeatsWarning={
							!config?.unrestrictedEnrollments && courseInfo.limitedSeatLicenses
						}
						setErrorMessage={setErrorMessage}
					/>
				</section>
			)}
		</section>
	);
}
